import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User, UserRole, RolePermissions } from '../types';
import { authService, normalizeAuthUser } from '../services/authService';
import { apiService } from '../services/api';
import { STORAGE_KEYS } from '../constants';
import { USER_ROLES } from '../constants';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: RolePermissions;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  hasPermission: (permission: keyof RolePermissions) => boolean;
  hasRole: (role: UserRole) => boolean;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'REFRESH_USER'; payload: User }
  | { type: 'SET_LOADING'; payload: boolean };

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  permissions: {
    canViewCompanies: false,
    canManageCompanies: false,
    canViewUsers: false,
    canManageUsers: false,
    canViewJobs: false,
    canManageJobs: false,
    canViewTasks: false,
    canManageTasks: false,
    canViewTeams: false,
    canManageTeams: false,
    canViewCustomers: false,
    canManageCustomers: false,
    canViewDashboard: false,
    canViewReports: false,
  },
};

function getRolePermissions(role: UserRole, companyId?: number | null): RolePermissions {
  switch (role) {
    case USER_ROLES.ADMIN:
      return {
        canViewCompanies: true,
        canManageCompanies: true,
        canViewUsers: true,
        canManageUsers: true,
        canViewJobs: true,
        canManageJobs: true,
        canViewTasks: true,
        canManageTasks: true,
        canViewTeams: true,
        canManageTeams: true,
        canViewCustomers: true,
        canManageCustomers: true,
        canViewDashboard: true,
        canViewReports: true,
      };
    case USER_ROLES.SUPERVISOR:
      return {
        canViewCompanies: true,
        canManageCompanies: false,
        canViewUsers: true,
        canManageUsers: false,
        canViewJobs: true,
        canManageJobs: true,
        canViewTasks: true,
        canManageTasks: true,
        canViewTeams: true,
        canManageTeams: true,
        canViewCustomers: false,
        canManageCustomers: false,
        canViewDashboard: true,
        canViewReports: true,
      };
    case USER_ROLES.WORKER:
      return {
        canViewCompanies: false,
        canManageCompanies: false,
        canViewUsers: false,
        canManageUsers: false,
        canViewJobs: true,
        canManageJobs: false,
        canViewTasks: true,
        canManageTasks: true,
        canViewTeams: false,
        canManageTeams: false,
        canViewCustomers: false,
        canManageCustomers: false,
        canViewDashboard: true,
        canViewReports: false,
      };
    default:
      return initialState.permissions;
  }
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
      };
    case 'LOGIN_SUCCESS':
      // Normalize role to ensure permissions resolve correctly even if API returns different casing
      {
        const normalizedRole = (String(action.payload.role || '') as string).toLowerCase() as UserRole;
        const updatedUser = { ...action.payload, role: normalizedRole } as User;
      return {
        ...state,
        user: updatedUser,
        isAuthenticated: true,
        isLoading: false,
        permissions: getRolePermissions(updatedUser.role, updatedUser.company_id),
      };
      }
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        permissions: initialState.permissions,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        permissions: initialState.permissions,
      };
    case 'REFRESH_USER':
      {
        const normalizedRole = (String(action.payload.role || '') as string).toLowerCase() as UserRole;
        const updatedUser = { ...action.payload, role: normalizedRole } as User;
        return {
          ...state,
          user: updatedUser,
          isAuthenticated: true,
          isLoading: false,
          permissions: getRolePermissions(updatedUser.role, updatedUser.company_id),
        };
      }
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    // Check if user is already logged in
    const storedUser = authService.getStoredUser();
    const storedToken = authService.getStoredToken();

    if (storedUser && storedToken) {
      // Immediately restore from localStorage so UI (nav, permissions) renders instantly
      dispatch({ type: 'REFRESH_USER', payload: storedUser });

      // Then validate token in the background — only logout if truly invalid
      apiService.get('/auth/me')
        .then((response) => {
          if (response.success && response.data) {
            // Update with fresh data from server
            dispatch({ type: 'REFRESH_USER', payload: normalizeAuthUser(response.data) });
          } else {
            localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER_DATA);
            dispatch({ type: 'LOGOUT' });
          }
        })
        .catch(() => {
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER_DATA);
          dispatch({ type: 'LOGOUT' });
        });
    } else {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      const response = await authService.login({ email, password, user_type: 'admin' });
      dispatch({ type: 'LOGIN_SUCCESS', payload: response.user });
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };

  const refreshUser = async () => {
    try {
      const user = await authService.getCurrentUser();
      // Persist refreshed user for future loads
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      dispatch({ type: 'REFRESH_USER', payload: user });
    } catch (error) {
      console.error('Refresh user error:', error);
      // Do not logout on refresh failures; keep current session intact
      // This avoids clearing permissions and losing navigation unexpectedly
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const hasPermission = (permission: keyof RolePermissions): boolean => {
    // While auth state is loading, hide restricted UI elements until auth resolves
    if (state.isLoading) {
      return false;
    }
    return state.permissions[permission];
  };

  const hasRole = (role: UserRole): boolean => {
    return state.user?.role === role;
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    refreshUser,
    hasPermission,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
